import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  CheckCircle2, AlertTriangle, Copy, RefreshCw, Loader2, ShieldCheck, Server,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

// Human descriptions for each tracked env var, so the admin understands which
// integration breaks if it's missing.
const ENV_META = {
  OAUTH_PUBLIC_HOST:           { critical: true,  label: 'OAuth public host',          why: 'Domain used for OAuth redirect URIs (must match every provider whitelist).' },
  APP_SECRET_KEY:              { critical: true,  label: 'App secret key (Fernet)',    why: 'Encrypts/decrypts stored OAuth tokens at rest. Without it OAuth callbacks 500.' },
  MONGO_URL:                   { critical: true,  label: 'MongoDB connection',         why: 'Database connection string. Missing → app cannot start.' },
  DB_NAME:                     { critical: true,  label: 'Database name',              why: 'Selects which Mongo database to use.' },
  STRIPE_SECRET_KEY:           { critical: false, label: 'Stripe secret key',          why: 'Required for paid-plan checkout and subscription webhooks.' },
  EMERGENT_LLM_KEY:            { critical: false, label: 'Emergent LLM key',           why: 'Powers AI text & image generation (fallback for OpenAI).' },
  RESEND_API_KEY:              { critical: false, label: 'Resend API key',             why: 'Sends transactional email (verification, password reset, payment receipts).' },
  TURNSTILE_SECRET_KEY:        { critical: false, label: 'Cloudflare Turnstile',       why: 'Server-side captcha verification on signup / login.' },
  FRONTEND_URL:                { critical: false, label: 'Frontend URL',               why: 'Used in email links and OAuth fallback when OAUTH_PUBLIC_HOST is absent.' },
  EMAIL_VERIFICATION_REQUIRED: { critical: false, label: 'Email verification',         why: 'When true, new users must verify their email before logging in.' },
};

const PROVIDER_DOCS = {
  facebook:  { name: 'Facebook',  url: 'https://developers.facebook.com/apps',     where: 'Facebook → App Settings → Facebook Login for Business → Settings → Valid OAuth Redirect URIs' },
  instagram: { name: 'Instagram', url: 'https://developers.facebook.com/apps',     where: 'Facebook → App Settings → Instagram → API setup with Facebook Login → Valid OAuth Redirect URIs' },
  linkedin:  { name: 'LinkedIn',  url: 'https://www.linkedin.com/developers/apps', where: 'LinkedIn → Your App → Auth tab → Authorized redirect URLs' },
  youtube:   { name: 'YouTube',   url: 'https://console.cloud.google.com/apis/credentials', where: 'Google Cloud Console → Credentials → OAuth 2.0 Client → Authorized redirect URIs' },
  tiktok:    { name: 'TikTok',    url: 'https://developers.tiktok.com/apps',       where: 'TikTok for Developers → Your App → Login Kit → Redirect URI' },
  bluesky:   { name: 'Bluesky',   url: 'https://bsky.app',                          where: 'No admin OAuth config — Bluesky uses per-user app passwords.' },
};

export const DeploymentCheckPanel = () => {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/deployment-check');
      setData(res.data);
    } catch {
      toast.error('Failed to load deployment check');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const copy = (s) => {
    navigator.clipboard.writeText(s);
    toast.success('Copied to clipboard');
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
      </div>
    );
  }

  const overallOk = data.deployment.all_critical_env_set && data.encrypt_health?.ok;

  return (
    <div className="space-y-4" data-testid="deployment-check-panel">
      {/* Header / overall status */}
      <Card className={overallOk ? 'border-emerald-200' : 'border-amber-300'}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            {overallOk ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            )}
            Deployment status: {overallOk ? 'Healthy' : 'Needs attention'}
          </CardTitle>
          <CardDescription className="text-xs">
            Public host: <strong>{data.deployment.public_host || '(not set)'}</strong>
            {' · '}Frontend: <strong>{data.deployment.frontend_url || '(not set)'}</strong>
            {' · '}Checked at {new Date(data.checked_at).toLocaleString()}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-deployment-check">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            Re-check
          </Button>
        </CardContent>
      </Card>

      {/* Encryption health */}
      <Card className="border-zinc-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-zinc-500" />
            Token encryption
          </CardTitle>
        </CardHeader>
        <CardContent className="text-xs">
          {data.encrypt_health?.ok ? (
            <p className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Encrypt + decrypt round-trip succeeded with current APP_SECRET_KEY.
            </p>
          ) : (
            <p className="flex items-start gap-2 text-red-700">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                <strong>Encryption broken:</strong> {data.encrypt_health?.error || 'unknown error'}.
                Set <code className="bg-zinc-100 px-1 rounded">APP_SECRET_KEY</code> in the deployment environment variables and redeploy.
              </span>
            </p>
          )}
        </CardContent>
      </Card>

      {/* Env vars */}
      <Card className="border-zinc-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Server className="w-4 h-4 text-zinc-500" />
            Environment variables
          </CardTitle>
          <CardDescription className="text-xs">
            Critical vars must be set on every deploy. Secret values are never returned by the server.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-1.5">
          {Object.entries(data.env).map(([name, st]) => {
            const meta = ENV_META[name] || { label: name, critical: false, why: '' };
            const ok = st.set;
            return (
              <div
                key={name}
                className="flex items-start gap-3 text-xs py-1.5 px-2 rounded border border-zinc-100"
                data-testid={`env-row-${name}`}
              >
                <span className="mt-0.5">
                  {ok ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  ) : meta.critical ? (
                    <AlertTriangle className="w-4 h-4 text-red-600" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                  )}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium flex items-center gap-2">
                    <code className="bg-zinc-100 px-1 py-0.5 rounded text-[10px]">{name}</code>
                    <span className="text-zinc-600 font-normal">{meta.label}</span>
                    {meta.critical && !ok && (
                      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-[9px]">CRITICAL</Badge>
                    )}
                  </p>
                  <p className="text-[10px] text-zinc-500 mt-0.5">{meta.why}</p>
                  {st.value && (
                    <p className="text-[10px] text-zinc-400 mt-0.5 font-mono truncate">→ {st.value}</p>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Provider redirect URIs */}
      <Card className="border-zinc-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Copy className="w-4 h-4 text-zinc-500" />
            OAuth redirect URIs (paste into each provider's app config)
          </CardTitle>
          <CardDescription className="text-xs">
            These are the exact byte-for-byte values your backend sends to each provider on OAuth start.
            Each platform's whitelist <strong>must contain its entry exactly</strong> (no trailing slash, no www).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {data.platforms.map((p) => {
            const docs = PROVIDER_DOCS[p.platform] || { name: p.platform, where: '' };
            return (
              <div
                key={p.platform}
                className="text-xs border border-zinc-100 rounded p-2"
                data-testid={`redirect-row-${p.platform}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium capitalize">{docs.name}</span>
                  {p.needs_admin_creds ? (
                    p.configured ? (
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[9px]">Admin creds set</Badge>
                    ) : (
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[9px]">Admin creds missing</Badge>
                    )
                  ) : (
                    <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200 text-[9px]">No admin setup required</Badge>
                  )}
                </div>
                {p.redirect_uri ? (
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-zinc-50 px-2 py-1 rounded text-[10px] font-mono break-all">
                      {p.redirect_uri}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-[10px]"
                      onClick={() => copy(p.redirect_uri)}
                      data-testid={`copy-redirect-${p.platform}`}
                    >
                      <Copy className="w-3 h-3 mr-1" />
                      Copy
                    </Button>
                  </div>
                ) : (
                  <p className="text-[10px] text-zinc-500 italic">{docs.where}</p>
                )}
                {p.redirect_uri && (
                  <p className="text-[10px] text-zinc-500 mt-1">📍 {docs.where}</p>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
};

export default DeploymentCheckPanel;
